/**
 * 🔧 HEALTH DASHBOARD DATA PROCESSOR
 * 
 * Processes health data for proper display on the frontend dashboard
 * Fixes common display issues like water glasses, health scores, etc.
 */

import { HealthScoreCalculator } from './healthScoreCalculator';

export class HealthDashboardProcessor {
  constructor() {
    this.healthCalculator = new HealthScoreCalculator();
  }

  /**
   * 📊 PROCESS HEALTH DATA FOR DASHBOARD DISPLAY
   */
  processHealthDataForDashboard(rawHealthData) {
    // Calculate overall health score
    const healthScore = this.healthCalculator.calculateOverallHealthScore(rawHealthData);
    
    // Process individual metrics
    const processedData = {
      // Overall health score
      overallHealthScore: {
        percentage: healthScore.overallScore,
        level: healthScore.level,
        lastSync: rawHealthData.timestamp || new Date().toISOString()
      },

      // Vital signs
      heartRate: this.processHeartRate(rawHealthData.healthMetrics?.vitalSigns?.heartRate),
      steps: this.processSteps(rawHealthData.healthMetrics?.activity?.steps),
      calories: this.processCalories(rawHealthData.healthMetrics?.activity?.activeCalories),
      water: this.processHydration(rawHealthData.healthMetrics?.wellness?.hydration), // Fixed water display
      sleep: this.processSleep(rawHealthData.healthMetrics?.sleep?.sleep),
      stress: this.processStress(rawHealthData.healthMetrics?.wellness?.stress),
      temperature: this.processTemperature(rawHealthData.healthMetrics?.vitalSigns?.bodyTemperature),
      medications: this.processMedications(rawHealthData.healthMetrics?.medications),

      // Connected devices
      connectedDevices: this.processConnectedDevices(rawHealthData.data?.metadata?.deviceInfo),

      // Activity breakdown for data completeness
      dataCompleteness: this.processDataCompleteness(rawHealthData, healthScore.breakdown),

      // Additional dashboard info
      activePlans: this.getActivePlans(rawHealthData),
      
      // Health score breakdown for detailed view
      healthScoreBreakdown: healthScore.breakdown,
      recommendations: healthScore.recommendations
    };

    return processedData;
  }

  /**
   * 💓 PROCESS HEART RATE
   */
  processHeartRate(heartRateData) {
    if (!heartRateData) return { value: '--', unit: 'bpm', status: 'no-data' };

    return {
      value: heartRateData.value,
      unit: 'bpm',
      status: this.getHeartRateStatus(heartRateData.value),
      trend: heartRateData.trend?.direction || 'stable',
      confidence: Math.round((heartRateData.confidence || 0) * 100)
    };
  }

  /**
   * 🚶‍♀️ PROCESS STEPS
   */
  processSteps(stepsData) {
    if (!stepsData) return { value: '--', goal: 10000, percentage: 0 };

    return {
      value: stepsData.value.toLocaleString(), // Format with commas
      goal: stepsData.goal || 10000,
      percentage: Math.round((stepsData.value / (stepsData.goal || 10000)) * 100),
      status: stepsData.value >= (stepsData.goal || 10000) ? 'achieved' : 'in-progress'
    };
  }

  /**
   * 🔥 PROCESS CALORIES
   */
  processCalories(caloriesData) {
    if (!caloriesData) return { value: '--', unit: 'kcal', goal: 600 };

    return {
      value: caloriesData.value,
      unit: 'kcal',
      goal: caloriesData.goal || 600,
      percentage: Math.round((caloriesData.value / (caloriesData.goal || 600)) * 100),
      status: caloriesData.value >= (caloriesData.goal || 600) ? 'achieved' : 'in-progress'
    };
  }

  /**
   * 💧 PROCESS HYDRATION (FIXED WATER GLASSES DISPLAY)
   */
  processHydration(hydrationData) {
    if (!hydrationData) {
      return { 
        value: 0, 
        glasses: 0, 
        goal: 8, 
        unit: '/8',
        percentage: 0,
        status: 'low'
      };
    }

    // Calculate glasses (250ml per glass standard)
    const mlPerGlass = 250;
    const glassesConsumed = Math.floor(hydrationData.value / mlPerGlass);
    const dailyGlassGoal = 8; // Standard 8 glasses per day
    
    return {
      value: glassesConsumed,
      glasses: glassesConsumed,
      goal: dailyGlassGoal,
      unit: `/${dailyGlassGoal}`,
      percentage: Math.round((glassesConsumed / dailyGlassGoal) * 100),
      status: glassesConsumed >= dailyGlassGoal ? 'achieved' : 
              glassesConsumed >= dailyGlassGoal * 0.75 ? 'good' : 'low',
      milliliters: hydrationData.value
    };
  }

  /**
   * 😴 PROCESS SLEEP
   */
  processSleep(sleepData) {
    if (!sleepData) return { value: '--', unit: 'h', quality: 'unknown' };

    return {
      value: sleepData.totalSleep,
      unit: 'h',
      quality: sleepData.quality || 'unknown',
      efficiency: sleepData.sleepEfficiency || 0,
      bedTime: sleepData.bedTime,
      wakeTime: sleepData.wakeTime
    };
  }

  /**
   * 😰 PROCESS STRESS
   */
  processStress(stressData) {
    if (!stressData) return { value: '--', level: 'unknown' };

    return {
      value: stressData.value,
      level: stressData.level || this.getStressLevel(stressData.value),
      hrv: stressData.hrv
    };
  }

  /**
   * 🌡️ PROCESS TEMPERATURE
   */
  processTemperature(temperatureData) {
    if (!temperatureData) return { value: '--', unit: '°F', status: 'normal' };

    return {
      value: temperatureData.value,
      unit: temperatureData.unit || '°F',
      status: this.getTemperatureStatus(temperatureData.value),
      variation: temperatureData.variation
    };
  }

  /**
   * 💊 PROCESS MEDICATIONS (PLACEHOLDER)
   */
  processMedications(medicationsData) {
    // This would integrate with your medication tracking system
    return {
      active: 9, // Placeholder - replace with actual data
      taken: 7,
      pending: 2,
      status: 'on-track'
    };
  }

  /**
   * 📱 PROCESS CONNECTED DEVICES
   */
  processConnectedDevices(deviceInfo) {
    if (!deviceInfo) return [];

    return [{
      name: deviceInfo.deviceModel || 'Unknown Device',
      type: deviceInfo.source || 'unknown',
      status: 'connected',
      batteryLevel: deviceInfo.batteryLevel || 0,
      lastSync: deviceInfo.syncTime || new Date().toISOString()
    }];
  }

  /**
   * 📊 PROCESS DATA COMPLETENESS
   */
  processDataCompleteness(rawHealthData, scoreBreakdown) {
    const categories = {
      Activity: this.hasActivityData(rawHealthData.healthMetrics?.activity),
      Vitals: this.hasVitalsData(rawHealthData.healthMetrics?.vitalSigns),
      Sleep: this.hasSleepData(rawHealthData.healthMetrics?.sleep),
      Nutrition: this.hasNutritionData(rawHealthData.healthMetrics?.nutrition),
      Mental: this.hasMentalHealthData(rawHealthData.healthMetrics?.wellness)
    };

    const completedCategories = Object.values(categories).filter(Boolean).length;
    const totalCategories = Object.keys(categories).length;
    const completenessPercentage = Math.round((completedCategories / totalCategories) * 100);

    return {
      percentage: completenessPercentage,
      categories,
      breakdown: scoreBreakdown
    };
  }

  /**
   * 🎯 GET ACTIVE PLANS
   */
  getActivePlans(rawHealthData) {
    // This would integrate with your healthcare plans system
    return {
      enrolled: 5, // Placeholder
      active: ['Diabetes Care', 'Heart Health', 'Weight Loss', 'Fitness', 'Sleep Wellness']
    };
  }

  /**
   * 🔍 HELPER METHODS
   */
  getHeartRateStatus(heartRate) {
    if (heartRate < 60) return 'low';
    if (heartRate > 100) return 'high';
    return 'normal';
  }

  getStressLevel(stressValue) {
    if (stressValue <= 30) return 'low';
    if (stressValue <= 60) return 'moderate';
    return 'high';
  }

  getTemperatureStatus(temperature) {
    if (temperature < 97.8) return 'low';
    if (temperature > 99.1) return 'high';
    return 'normal';
  }

  hasActivityData(activity) {
    return !!(activity?.steps || activity?.activeCalories || activity?.activeMinutes);
  }

  hasVitalsData(vitals) {
    return !!(vitals?.heartRate || vitals?.bloodOxygen || vitals?.bodyTemperature);
  }

  hasSleepData(sleep) {
    return !!(sleep?.sleep?.totalSleep);
  }

  hasNutritionData(nutrition) {
    return !!(nutrition && Object.keys(nutrition).length > 0);
  }

  hasMentalHealthData(wellness) {
    return !!(wellness?.stress || wellness?.mindfulness);
  }
}

/**
 * 🧪 DEMO WITH YOUR ACTUAL DATA
 */
export function demonstrateDashboardProcessing() {
  const processor = new HealthDashboardProcessor();
  
  // Your actual Apple Watch data
  const appleWatchData = {
    "userId": "f6d2b38c-cc5a-4072-bc28-2b48c1d438ba",
    "timestamp": "2025-08-13T14:00:00.000Z",
    "data": {
      "metadata": {
        "deviceInfo": {
          "platform": "HealthKit API",
          "source": "apple-watch",
          "deviceModel": "Apple Watch Series 9",
          "batteryLevel": 76,
          "syncTime": "2025-08-13T14:00:00.000Z"
        }
      }
    },
    "healthMetrics": {
      "vitalSigns": {
        "heartRate": { "value": 99, "confidence": 0.95, "trend": { "direction": "decreasing" } },
        "bloodOxygen": { "value": 99, "confidence": 0.96 },
        "bodyTemperature": { "value": 99.2, "confidence": 0.99, "variation": "0.8" }
      },
      "activity": {
        "steps": { "value": 11256, "goal": 10000, "percentage": 1.13 },
        "activeCalories": { "value": 684, "goal": 600, "percentage": 1.14 },
        "activeMinutes": { "value": 76, "goal": 30, "percentage": 2.53 }
      },
      "sleep": {
        "sleep": {
          "totalSleep": 7.6,
          "sleepEfficiency": 86,
          "quality": "good",
          "bedTime": "22:09",
          "wakeTime": "7:25"
        }
      },
      "wellness": {
        "stress": { "value": 39, "level": "moderate", "hrv": 26.4 },
        "hydration": { "value": 2424, "goal": 2000, "percentage": 121 },
        "mindfulness": { "sessionCount": 1, "totalMinutes": 0 }
      }
    },
    "businessIntelligence": {
      "dataQuality": 0.9875,
      "completeness": 99,
      "reliability": "medium"
    }
  };

  const dashboardData = processor.processHealthDataForDashboard(appleWatchData);
  
  console.log('🏥 PROCESSED DASHBOARD DATA');
  console.log('============================');
  console.log(`Overall Health Score: ${dashboardData.overallHealthScore.percentage}% (${dashboardData.overallHealthScore.level})`);
  console.log(`Heart Rate: ${dashboardData.heartRate.value} ${dashboardData.heartRate.unit} (${dashboardData.heartRate.status})`);
  console.log(`Steps: ${dashboardData.steps.value} (${dashboardData.steps.percentage}% of goal)`);
  console.log(`Calories: ${dashboardData.calories.value} ${dashboardData.calories.unit} (${dashboardData.calories.percentage}% of goal)`);
  console.log(`💧 Water: ${dashboardData.water.value} ${dashboardData.water.unit} (${dashboardData.water.percentage}% of daily goal)`);
  console.log(`Sleep: ${dashboardData.sleep.value} ${dashboardData.sleep.unit} (${dashboardData.sleep.quality})`);
  console.log(`Stress: ${dashboardData.stress.value} (${dashboardData.stress.level})`);
  console.log(`Temperature: ${dashboardData.temperature.value} ${dashboardData.temperature.unit} (${dashboardData.temperature.status})`);
  console.log(`Data Completeness: ${dashboardData.dataCompleteness.percentage}%`);
  console.log(`Connected Devices: ${dashboardData.connectedDevices[0]?.name} (${dashboardData.connectedDevices[0]?.status})`);

  return dashboardData;
}

// Note: Demo runner disabled in browser bundles; guard for Node-only execution
if (typeof require !== 'undefined' && typeof module !== 'undefined') {
  if (require.main === module) {
    demonstrateDashboardProcessing();
  }
}
